import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  Pagination,
  message as antdMessage,
  Typography,
  Tag,
  Space,
  Spin,
  Row,
  Col,
  Modal,
  Radio,
  Divider,
  Popover,
  Tooltip,
  message,
} from "antd";
import {
  ReloadOutlined,
  LeftOutlined,
  RightOutlined,
  SettingOutlined,
  EyeOutlined,
  FilterOutlined,
  ArrowLeftOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  BACKEND_CHUNK_SIZE,
  DEFAULT_ROWS,
  ROWS_PER_PAGE_OPTIONS,
  SERVER_FILTER_THRESHOLD,
  statusTagProps,
  PARENT_LABELS,
} from "../../../util/helper";

import { useBatchData } from "./hooks/useBatchData";
import { useMoleculeImages } from "./hooks/useMoleculeImages";
import { useDynamicColumns } from "./hooks/useDynamicColumns";
import { useColumnManager } from "./hooks/useColumnManager";
import { useAdvancedFilter } from "./hooks/useAdvancedFilter";

import { MoleculeTable } from "./components/MoleculeTable";
import { MoleculeGrid } from "./components/MoleculeGrid";
import { AdvancedFilterDrawer } from "./components/AdvancedFilterDrawer";
import { ColumnSettingsPopover } from "./components/ColumnSettingsPopover";
import { ExperimentInfoDrawer } from "./components/ExperimentInfoDrawer";
import { getRequest, postRequest, putRequest } from "../../../util/service";
import { setBatchVisibleColumns } from "../../../store/batchResultSlice";
import BreadcrumbTrail from "../../shared/BreadcrumbTrail";

const { Text, Title } = Typography;

const OP_LABEL = {
  eq: "=",
  neq: "!=",
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<=",
  contains: "contains",
  is: "is",
  exists: "Exists",
  not_exists: "Not Exists",
  is_passed: "Is Passed",
  is_failed: "Is Failed",
  is_true: "Is True",
  is_false: "Is False",
};

const PARENT_COLOR = {
  all: "default",
  molprops: "green",
  glide: "volcano",
  phase: "geekblue",
  adme: "purple",
};

const DEFAULT_SORT_CONFIG = { columnKey: "reward", order: "descend" };

const isEmptySortValue = (value) => value === undefined || value === null || value === "";
const normalizeStatus = (status = "") => String(status).toLowerCase().replace(/[_-]+/g, " ").trim();
const isReTriggerBlockedStatus = (status = "") =>
  status === "failed" || status === "in progress" || status.includes("terminate");

const compareSortValues = (left, right) => {
  if (typeof left === "boolean" && typeof right === "boolean") {
    return Number(left) - Number(right);
  }

  const leftNum = typeof left === "number" ? left : Number(left);
  const rightNum = typeof right === "number" ? right : Number(right);
  if (Number.isFinite(leftNum) && Number.isFinite(rightNum)) {
    return leftNum - rightNum;
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const buildDownloadFilters = (rules = []) =>
  (Array.isArray(rules) ? rules : []).map((rule, index) => {
    const nextRule = {
      parent: rule.parent,
      child: rule.child,
      op: rule.op,
    };

    if (rule.value !== undefined && rule.value !== null && rule.value !== "") {
      nextRule.value = rule.value;
    }

    if (index > 0 && rule.glue) {
      nextRule.glue = rule.glue;
    }

    return nextRule;
  });

const downloadLocalTSV = (dataToDownload, batchId) => {
  const headers = ["molecule_id", "smile", "reward"];
  const header = headers.join("\t");
  const rows = dataToDownload
    .map((record) => headers.map((key) => String(record?.[key] ?? "")).join("\t"))
    .join("\n");
  const blob = new Blob([header + "\n" + rows], {
    type: "text/tab-separated-values;charset=utf-8",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `batch_${batchId}_details.tsv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

const getDownloadFileName = (contentDisposition, fallbackName) => {
  if (!contentDisposition) return fallbackName;

  const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].replace(/["']/g, ""));
    } catch {
      return utfMatch[1].replace(/["']/g, "");
    }
  }

  const standardMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return standardMatch?.[1] || fallbackName;
};

export default function BatchDetailsPage() {
  const dispatch = useDispatch();
  const { projectId, batchId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [batchMeta, setBatchMeta] = useState(null);
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG);
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);
  const [selectedMap, setSelectedMap] = useState({});
  const [viewMode, setViewMode] = useState("table");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS);
  const [filterApplying, setFilterApplying] = useState(false);
  const [confirmTerminate, setConfirmTerminate] = useState(false);
  const [stableFilteredCount, setStableFilteredCount] = useState(0);
  const lastDispatchedColumnsKeyRef = useRef("");

  const filterStorageKey = useMemo(
    () => `batch_filters_${projectId}_${batchId}`,
    [projectId, batchId]
  );

  const {
    loading,
    historyData,
    backendMessage,
    statusMessage,
    chunkStart,
    nextChunkStart,
    totalFiltered,
    frontPage,
    setFrontPage,
    fetchChunk,
    requiresServerFiltering,
    lastFiltersKey,
  } = useBatchData(projectId, batchId);

  const dynamicColumnGroups = useDynamicColumns(historyData);
  const columnManager = useColumnManager(dynamicColumnGroups);
  const filterManager = useAdvancedFilter(historyData, dynamicColumnGroups, filterStorageKey);

  const batchStatus = batchMeta?.b_status || state?.batchStatus || "";
  const normalizedBatchStatus = normalizeStatus(batchStatus);
  const parentBatchId = batchMeta?.parent_batch_id || state?.parent_batch_id || null;

  useEffect(() => {
    const nextKey = JSON.stringify(columnManager.visibleColumns || []);
    if (lastDispatchedColumnsKeyRef.current === nextKey) return;
    lastDispatchedColumnsKeyRef.current = nextKey;
    dispatch(setBatchVisibleColumns(columnManager.visibleColumns));
  }, [columnManager.visibleColumns, dispatch]);

  const serverFiltersKey = useMemo(
    () => JSON.stringify(filterManager.rulesApplied || []),
    [filterManager.rulesApplied]
  );

  useEffect(() => {
    if (!requiresServerFiltering) return;
    if (serverFiltersKey === lastFiltersKey) return;
    fetchChunk({ start: 0, filters: filterManager.rulesApplied });
  }, [requiresServerFiltering, serverFiltersKey, lastFiltersKey, fetchChunk, filterManager.rulesApplied]);

  useEffect(() => {
    if (!filterApplying) return;
    if (requiresServerFiltering) {
      if (!loading && lastFiltersKey === serverFiltersKey) {
        setFilterApplying(false);
      }
      return;
    }
    setFilterApplying(false);
  }, [filterApplying, requiresServerFiltering, loading, lastFiltersKey, serverFiltersKey]);

  const fetchBatchMeta = useCallback(async () => {
    if (!projectId || !batchId) return;

    try {
      const response = await getRequest(`/riva/moleculegenbatches/${projectId}`);
      const batches = Array.isArray(response?.data) ? response.data : [];
      const currentBatch = batches.find((item) => String(item.b_record_id) === String(batchId));
      setBatchMeta(currentBatch || null);
    } catch {
      setBatchMeta(null);
    }
  }, [projectId, batchId]);

  useEffect(() => {
    fetchBatchMeta();
  }, [fetchBatchMeta]);

  useEffect(() => {
    setSortConfig(DEFAULT_SORT_CONFIG);
  }, [batchId]);

  const sortAccessors = useMemo(() => {
    const accessors = {
      reward: (row) => row.reward,
    };

    dynamicColumnGroups.dynamicGlideColumns.forEach((column) => {
      accessors[column.key] = (row) =>
        (row.glide || []).find(
          (item) => item.file === column.file && item.ph_threshold === column.ph_threshold
        )?.score;
    });

    dynamicColumnGroups.dynamicPhaseColumns.forEach((column) => {
      accessors[column.key] = (row) =>
        (row.phase || []).find(
          (item) => item.file === column.file && item.ph_threshold === column.ph_threshold
        )?.matched_ligand_sites;
    });

    dynamicColumnGroups.dynamicAdmeColumns.forEach((column) => {
      accessors[column.key] = (row) => row?.[column.fullKey]?.value;
    });

    dynamicColumnGroups.dynamicMolProps.forEach((column) => {
      accessors[column.key] = (row) => row?.[column.sourceKey];
    });

    return accessors;
  }, [dynamicColumnGroups]);

  useEffect(() => {
    if (!sortConfig.columnKey) return;

    const visibleSortKeys = new Set(columnManager.visibleColumns);
    if (!visibleSortKeys.has(sortConfig.columnKey)) {
      setSortConfig(DEFAULT_SORT_CONFIG);
    }
  }, [columnManager.visibleColumns, sortConfig.columnKey]);

  const sortedFilteredData = useMemo(() => {
    if (!sortConfig.columnKey || !sortConfig.order) {
      return filterManager.filteredData;
    }

    const accessor = sortAccessors[sortConfig.columnKey];
    if (typeof accessor !== "function") {
      return filterManager.filteredData;
    }

    const direction = sortConfig.order === "descend" ? -1 : 1;

    return filterManager.filteredData
      .map((row, index) => ({ row, index }))
      .sort((leftItem, rightItem) => {
        const leftValue = accessor(leftItem.row);
        const rightValue = accessor(rightItem.row);
        const leftEmpty = isEmptySortValue(leftValue);
        const rightEmpty = isEmptySortValue(rightValue);

        if (leftEmpty || rightEmpty) {
          if (leftEmpty && rightEmpty) return leftItem.index - rightItem.index;
          return leftEmpty ? 1 : -1;
        }

        const result = compareSortValues(leftValue, rightValue);
        if (result !== 0) {
          return result * direction;
        }

        return leftItem.index - rightItem.index;
      })
      .map((item) => item.row);
  }, [filterManager.filteredData, sortAccessors, sortConfig]);

  const pagedData = useMemo(() => {
    const startIndex = (frontPage - 1) * rowsPerPage;
    return sortedFilteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedFilteredData, frontPage, rowsPerPage]);

  const { images } = useMoleculeImages(pagedData);

  const getRuleLabels = (rule) => {
    const parentLabel = PARENT_LABELS[rule.parent] || rule.parent;
    const childOptions = filterManager.childOptionsByParent[rule.parent] || [];
    const childLabel = childOptions.find((opt) => opt.value === rule.child)?.label || rule.child;
    return { parentLabel, childLabel };
  };

  const handleRealtimeDelete = (indexToDelete) => {
    const newRules = filterManager.rulesDraft.filter((_, index) => index !== indexToDelete);
    setFilterApplying(true);
    filterManager.setRulesDraft(newRules);
    filterManager.setRulesApplied(newRules);
    setFrontPage(1);
  };

  const handleRealtimeClear = () => {
    setFilterApplying(true);
    filterManager.setRulesDraft([]);
    filterManager.setRulesApplied([]);
    setFrontPage(1);
    message.info("Filters cleared");
  };

  const handleGoToParent = () => {
    if (!parentBatchId) return;

    navigate(`/project/${projectId}/batch/${parentBatchId}`, {
      state: {
        projectName: state?.projectName,
        owners: state?.owners,
        teamMembers: state?.teamMembers,
        description: state?.description,
      },
    });
  };

  const handleReTrigger = async () => {
    try {
      message.loading({ content: "Loading experiment data for re-trigger...", key: "retrigger" });
      const response = await getRequest(`/riva/moleculegenbatches/${projectId}/molecules/${batchId}/batchinputs`);
      if (!response?.data) throw new Error("Could not load experiment data.");

      navigate(`/attributes/${batchId}/edit`, {
        state: {
          ...state,
          projectId,
          batchInfoData: response.data,
          isReTrigger: true,
        },
      });
      message.destroy("retrigger");
    } catch (err) {
      message.error({ content: err.message || "Failed to prepare re-trigger.", key: "retrigger" });
    }
  };

  const handleSortChange = (nextSort) => {
    setSortConfig(nextSort);
    setFrontPage(1);
  };

  const handleCopyToClipboard = (text, successMessage = "SMILES copied") =>
    navigator.clipboard
      .writeText(text)
      .then(() => antdMessage.success(successMessage))
      .catch(() => antdMessage.error("Copy failed"));

  const selectAllFiltered = (checked) => {
    if (checked) {
      const next = sortedFilteredData.reduce((acc, row) => ({ ...acc, [row.molecule_id]: row }), {});
      setSelectedMap(next);
      return;
    }
    setSelectedMap({});
  };

  const handleGridItemCheck = (checked, row) =>
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (checked) next[row.molecule_id] = row;
      else delete next[row.molecule_id];
      return next;
    });

  const terminateJob = async () => {
    setConfirmTerminate(false);
    try {
      await putRequest(`/riva/moleculegenbatches/${projectId}/molecules/${batchId}`);
      antdMessage.success("Terminate request sent");
      navigate(-1);
    } catch (err) {
      antdMessage.error(err.message || "Terminate failed");
    }
  };

  const handleCreateSelection = () => {
    const selectedMolecules = Object.values(selectedMap);
    if (!selectedMolecules.length) return;
    navigate(`/project/${projectId}/batch/${batchId}/submit`, {
      state: {
        selectedMolecules,
        filterCriteria: filterManager.rulesApplied,
      },
    });
  };

  const handleHeaderSelectAll = (selected) => {
    const next = selected
      ? sortedFilteredData.reduce((acc, row) => ({ ...acc, [row.molecule_id]: row }), {})
      : {};
    setSelectedMap(next);
  };

  const downloadTSV = async () => {
    const selectedRows = Object.values(selectedMap);
    const dataToDownload = selectedRows.length > 0 ? selectedRows : sortedFilteredData;

    if (!dataToDownload.length && displayedFilteredCount === 0) {
      return antdMessage.info("No data to download");
    }

    const shouldUseBackendDownload =
      selectedRows.length === 0 &&
      (requiresServerFiltering || totalFiltered > SERVER_FILTER_THRESHOLD);

    if (!shouldUseBackendDownload) {
      downloadLocalTSV(dataToDownload, batchId);
      return;
    }

    const messageKey = "batch-download";
    antdMessage.loading({ content: "Preparing download...", key: messageKey });

    try {
      const response = await postRequest("/riva/moleculegenbatches/molecules/download", {
        projectId,
        batchId,
        filters: buildDownloadFilters(filterManager.rulesApplied),
      });

      const downloadUrl =
        typeof response?.data === "string"
          ? response.data
          : (response?.data?.url || response?.url || "");

      if (!downloadUrl) {
        throw new Error(response?.message || "Could not get download link.");
      }

      const fileResponse = await fetch(downloadUrl);
      if (!fileResponse.ok) {
        throw new Error(`Failed to fetch file: ${fileResponse.statusText}`);
      }

      const blob = await fileResponse.blob();
      const fileName = getDownloadFileName(
        fileResponse.headers.get("content-disposition"),
        `batch_${batchId}_details.tsv`
      );

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      antdMessage.success({ content: "Download started", key: messageKey, duration: 2 });
    } catch (err) {
      antdMessage.error({ content: err.message || "Download failed", key: messageKey, duration: 2 });
    }
  };

  const filteredCount = sortedFilteredData.length;
  const shouldHoldDisplayedCount = requiresServerFiltering && (loading || serverFiltersKey !== lastFiltersKey);
  const displayedFilteredCount = shouldHoldDisplayedCount ? stableFilteredCount : filteredCount;
  const hasMolecules = displayedFilteredCount > 0;
  const totalOnPage = pagedData.length;
  const pageStart = (frontPage - 1) * rowsPerPage + 1;
  const pageEnd = pageStart + totalOnPage - 1;

  useEffect(() => {
    if (shouldHoldDisplayedCount) return;
    setStableFilteredCount(filteredCount);
  }, [filteredCount, shouldHoldDisplayedCount]);

  const displayStatusMessage = useMemo(() => {
    const rawMessage = statusMessage || backendMessage;
    if (!rawMessage) return "";
    if (typeof rawMessage === "string") return rawMessage;

    try {
      return JSON.stringify(rawMessage, null, 2);
    } catch {
      return String(rawMessage);
    }
  }, [statusMessage, backendMessage]);

  const displayStatusDetail = displayStatusMessage ||
    (loading ? "Loading status details..." : "No additional status message available.");

  return (
    <div style={{ padding: "24px", marginBottom: "24px", background: "#fff", position: "relative" }}>
      {filterApplying && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.45)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" tip="Applying filters..." />
        </div>
      )}

      <Row justify="space-between" align="middle" style={{ marginTop: "80px", marginBottom: 16 }}>
        <Col>
          <Space align="center" size="small">
            <Button onClick={() => navigate(-1)} icon={<ArrowLeftOutlined />} />
            <Title level={4} style={{ margin: 0 }}>{batchId}</Title>
            {batchStatus && <Tag {...statusTagProps(batchStatus)} />}
            <Popover
              content={(
                <div
                  style={{
                    maxWidth: 320,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <div style={{ flex: 1, whiteSpace: "pre-wrap" }}>{displayStatusDetail}</div>
                  <Tooltip title="Copy status details">
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyToClipboard(displayStatusDetail, "Status details copied")}
                    />
                  </Tooltip>
                </div>
              )}
              title={null}
              trigger="click"
            >
              <Button size="small" icon={<InfoCircleOutlined />}>
                Info
              </Button>
            </Popover>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchBatchMeta();
                fetchChunk({
                  start: 0,
                  filters: requiresServerFiltering ? filterManager.rulesApplied : {},
                });
              }}
            />
            <Tooltip title="Experiment inputs & downloads">
              <Button icon={<EyeOutlined />} onClick={() => setInfoDrawerOpen(true)}>
                Exp. Details
              </Button>
            </Tooltip>
            <Button
              disabled={!hasMolecules}
              onClick={() => navigate(`/project/${projectId}/batch/${batchId}/results`)}
            >
              Modeling & Analysis
            </Button>
          </Space>
        </Col>
        <Col>
          <Space>
            {parentBatchId && (
              <Button onClick={handleGoToParent}>Go to Parent ({parentBatchId})</Button>
            )}
            <Button
              type="primary"
              onClick={handleReTrigger}
              disabled={isReTriggerBlockedStatus(normalizedBatchStatus)}
            >
              Re-trigger Exp
            </Button>
            <Button
              type="primary"
              disabled={Object.keys(selectedMap).length === 0}
              onClick={handleCreateSelection}
            >
              Create Selection ({Object.keys(selectedMap).length})
            </Button>
            {normalizedBatchStatus === "in progress" && (
              <Button danger onClick={() => setConfirmTerminate(true)}>Terminate</Button>
            )}
          </Space>
        </Col>
      </Row>
      <BreadcrumbTrail />

      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Space>
            <Popover
              content={<ColumnSettingsPopover {...columnManager} dynamicColumnGroups={dynamicColumnGroups} />}
              title="Display Columns"
              trigger="click"
              placement="bottomRight"
              open={columnManager.colsPopoverVis}
              onOpenChange={columnManager.setColsPopoverVis}
            >
              <Button icon={<SettingOutlined />} disabled={!hasMolecules}>Columns</Button>
            </Popover>
            <Button
              icon={<FilterOutlined />}
              disabled={!hasMolecules}
              type={filterManager.rulesApplied.length ? "primary" : "default"}
              onClick={() => {
                filterManager.setRulesDraft(filterManager.rulesApplied);
                filterManager.setFilterDrawerOpen(true);
              }}
            >
              Filter
            </Button>
            <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
              <Radio.Button value="table">Table</Radio.Button>
              <Radio.Button value="grid">Grid</Radio.Button>
            </Radio.Group>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button disabled={!hasMolecules} onClick={downloadTSV} icon={<DownloadOutlined />}>Download</Button>
          </Space>
        </Col>
      </Row>

      {historyData.length > 0 && (
        <Row justify="space-between" align="middle" style={{ marginBottom: 8 }} gutter={[12, 8]}>
          <Col>
            <Text strong>Total Molecules: {displayedFilteredCount.toLocaleString()}</Text>
          </Col>
          <Col>
            <Text
              style={{
                color: "#d4380d",
                fontStyle: "italic",
                display: "block",
                textAlign: "right",
              }}
            >
              Made with Generative AI. Check Accuracy.
            </Text>
          </Col>
        </Row>
      )}

      <Divider style={{ margin: "8px 0 16px" }} />

      {filterManager.rulesApplied.length > 0 && (
        <div
          style={{
            marginBottom: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <Space wrap align="center">
            {filterManager.rulesApplied.map((rule, index) => {
              const { parentLabel, childLabel } = getRuleLabels(rule);
              const op = OP_LABEL[rule.op] || rule.op;
              const valueText = rule.value !== undefined && rule.value !== "" ? ` : ${rule.value}` : "";
              const childText = childLabel.toLowerCase() === "all" ? "" : ` : ${childLabel}`;
              const tagText = `${parentLabel}${childText} ${op}${valueText}`;

              return (
                <React.Fragment key={rule.id}>
                  {index > 0 && <Text strong>{(rule.glue || "and").toUpperCase()}</Text>}
                  <Tag color={PARENT_COLOR[rule.parent || "all"]}>{tagText}</Tag>
                </React.Fragment>
              );
            })}
          </Space>
          <Button type="link" onClick={handleRealtimeClear}>Clear All</Button>
        </div>
      )}

      {loading && !historyData.length ? (
        <div style={{ textAlign: "center", padding: 32 }}><Spin size="large" /></div>
      ) : historyData.length === 0 ? (
        <div style={{ textAlign: "center", padding: 32 }}><Text type="secondary">No records found</Text></div>
      ) : viewMode === "grid" ? (
        <MoleculeGrid
          loading={loading}
          pagedData={pagedData}
          images={images}
          selectedMap={selectedMap}
          onItemCheckChange={handleGridItemCheck}
          onSelectAllChange={(e) => selectAllFiltered(e.target.checked)}
          allItemsSelected={filteredCount > 0 && Object.keys(selectedMap).length === filteredCount}
          someItemsSelected={Object.keys(selectedMap).length > 0 && Object.keys(selectedMap).length < filteredCount}
          filteredDataLength={filteredCount}
          handleCopyToClipboard={handleCopyToClipboard}
        />
      ) : (
        <MoleculeTable
          loading={loading}
          pagedData={pagedData}
          selectedMap={selectedMap}
          onSelectionChange={setSelectedMap}
          visibleColumns={columnManager.visibleColumns}
          onSelectAll={handleHeaderSelectAll}
          images={images}
          dynamicColumnGroups={dynamicColumnGroups}
          handleCopyToClipboard={handleCopyToClipboard}
          sortConfig={sortConfig}
          onSortChange={handleSortChange}
        />
      )}

      {historyData.length > 0 && (
        <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
          <Col>
            <Space>
              {chunkStart > 0 && (
                <Button
                  icon={<LeftOutlined />}
                  onClick={() =>
                    fetchChunk({
                      start: Math.max(0, chunkStart - BACKEND_CHUNK_SIZE),
                      filters: requiresServerFiltering ? filterManager.rulesApplied : {},
                    })
                  }
                >
                  Prev {BACKEND_CHUNK_SIZE.toLocaleString()}
                </Button>
              )}
              {chunkStart + historyData.length < totalFiltered && (
                <Button
                  icon={<RightOutlined />}
                  onClick={() =>
                    fetchChunk({
                      start: nextChunkStart,
                      filters: requiresServerFiltering ? filterManager.rulesApplied : {},
                    })
                  }
                >
                  Next {Math.min(BACKEND_CHUNK_SIZE, totalFiltered - (chunkStart + historyData.length)).toLocaleString()}
                </Button>
              )}
            </Space>
          </Col>
          <Col>
            <Space align="center">
              <Text type="secondary">
                Rows {displayedFilteredCount ? `${pageStart}-${pageEnd}` : 0} of {displayedFilteredCount.toLocaleString()}
              </Text>
              <Pagination
                current={frontPage}
                pageSize={rowsPerPage}
                total={displayedFilteredCount}
                onChange={(page, size) => {
                  setFrontPage(page);
                  if (size !== rowsPerPage) setRowsPerPage(size);
                }}
                pageSizeOptions={ROWS_PER_PAGE_OPTIONS.map((opt) => String(opt.value))}
                showSizeChanger
              />
            </Space>
          </Col>
        </Row>
      )}

      <ExperimentInfoDrawer
        open={infoDrawerOpen}
        onClose={() => setInfoDrawerOpen(false)}
        projectId={projectId}
        onReTrigger={handleReTrigger}
        batchId={batchId}
        batchStatus={batchStatus}
      />
      <AdvancedFilterDrawer
        open={filterManager.filterDrawerOpen}
        onClose={() => filterManager.setFilterDrawerOpen(false)}
        rulesDraft={filterManager.rulesDraft}
        setRulesDraft={filterManager.setRulesDraft}
        rulesApplied={filterManager.rulesApplied}
        setRulesApplied={filterManager.setRulesApplied}
        childOptionsByParent={filterManager.childOptionsByParent}
        valueMetaByChild={filterManager.valueMetaByChild}
        getChildType={filterManager.getChildType}
        OPS_NUMERIC={filterManager.OPS_NUMERIC}
        OPS_TEXT={filterManager.OPS_TEXT}
        PARENT_LABELS={PARENT_LABELS}
        onApply={() => {
          setFilterApplying(true);
          filterManager.setRulesApplied(filterManager.rulesDraft);
          setFrontPage(1);
          filterManager.setFilterDrawerOpen(false);
        }}
        onClearRealtime={handleRealtimeClear}
        onDeleteRealtime={handleRealtimeDelete}
        onClear={() => {
          filterManager.setRulesDraft([]);
        }}
      />
      <Modal
        open={confirmTerminate}
        title="Terminate Job"
        onOk={terminateJob}
        onCancel={() => setConfirmTerminate(false)}
        okText="Terminate"
        okButtonProps={{ danger: true }}
      >
        Are you sure you want to terminate this job?
      </Modal>
    </div>
  );
}
